import {decodeUriComponentWithTryCatch} from 'coreUtils/url';

import {FIRST_PAGE, REFINEMENT_DISALLOW_LIST} from 'brand/app/constants';

/**
 * Represents PlpUrlModifier
 */
class PlpUrlModifier {
	protected history;
	protected location;

	/**
	 * Updates current URL with sorting, page and refinements
	 * @param {string} searchPageUrl - searchPageUrl
	 * @param {number} pageNumber
	 * @param {string} sort - sort
	 * @param {Object} appliedRefinements - appliedRefinements
	 */
	public updateUrl(
		searchPageUrl: string,
		pageNumber: number,
		sort: string,
		appliedRefinements: object,
	): void {
		if  (this?.location?.pathname) {
			const decodedSearchPageUrl = this.getComparableUrl(searchPageUrl);
			const decodedPathname = this.getComparableUrl(this.location.pathname);

			if (decodedSearchPageUrl === decodedPathname) {
				const params: URLSearchParams = this.getUrlSearchParams();

				if (pageNumber) {
					this.setPageParameter(params, pageNumber);
				}

				this.setSortParameter(params, sort);
				this.setRefinementsParameter(params, appliedRefinements);
				params.sort();
				this.setUrlSearchParams(params, searchPageUrl);
			}
		}
	}

	/**
	 * Update History
	 * @param {object} history - history
	 */
	public setHistory(history) {
		this.history = history;
	}

	/**
	 * Update Location
	 * @param {object} location - location
	 */
	public setLocation(location) {
		this.location = location;
	}

	/**
	 * Returns PLP url parameters
	 * @param {number} pageNumber
	 * @param {string} sort - sort
	 * @param {Object} appliedRefinements - appliedRefinements
	 * @param {URLSearchParams} params
	 */
	public getUrlParameters(
		pageNumber: number,
		sort: string,
		appliedRefinements: object
	) {
		const params: URLSearchParams = this.getUrlSearchParams();

		if (pageNumber) {
			this.setPageParameter(params, pageNumber);
		}

		this.setSortParameter(params, sort);
		this.setRefinementsParameter(params, appliedRefinements);
		params.sort();

		return params;
	}

	/**
	 * Set sorting rule parameter on the url
	 * @param {URLSearchParams} params
	 * @param {string} value
	 */
	private setSortParameter(params: URLSearchParams, value: string): void {
		if (value?.toString()) {
			params.set('srule', value);
		} else {
			params.delete('srule');
		}
	}

	/**
	 * Set page parameter on the url
	 * @param {URLSearchParams} params
	 * @param {number} value
	 */
	private setPageParameter(params: URLSearchParams, value: number): void {
		if (value <= FIRST_PAGE) {
			params.delete('page');
		} else {
			params.set('page', value.toString());
		}
	}

	/**
	 * Set refinements parameter on the url
	 * @param {URLSearchParams} params
	 * @param {Object} appliedRefinements
	 */
	private setRefinementsParameter(params: URLSearchParams, appliedRefinements: Object): void {
		const copyParams: URLSearchParams = new URLSearchParams(params.toString());
		const urlEntriesArray = Array.from(copyParams.entries());

		for (const [key] of urlEntriesArray) {
			if (key.includes('prefn') || key.includes('prefv')) {
				params.delete(key);
			}
		}

		let index = 1;

		Object.keys(appliedRefinements || {}).map((attribute) => {
			if (REFINEMENT_DISALLOW_LIST.indexOf(attribute) === -1) {
				//TODO: cover in model
				params.set(`prefn${index}`, attribute);
				params.set(`prefv${index}`, appliedRefinements[attribute]);

				index++;
			}
		});
	}

	/**
	 * Set url search params
	 * @param {URLSearchParams} params
	 * @param {string} path
	 */
	private setUrlSearchParams(params: URLSearchParams, path: string): void {
		let newUrl;
		const currentSearch = typeof window !== 'undefined' ? window.location.search : this.location.search;
		const currentUrl = `${path}${currentSearch}`;
		const parameterString = params.toString();

		if (parameterString !== '') {
			newUrl = `${path}?${parameterString}`;
		} else {
			newUrl = path;
		}

		if (
			this.getComparableUrl(currentUrl) !== this.getComparableUrl(newUrl) &&
			(this?.location?.pathname === this?.location?.state?.from || !this?.location?.state?.from)
		) {
			if (newUrl.includes('page=') || currentUrl.includes('page=')) {
				const fullUrl = parameterString ? `${path}?${parameterString}` : path;

				if (typeof window !== 'undefined') {
					window.history.replaceState(window.history.state, '', fullUrl);
				}
			} else {
				const locationObj = {
					pathname: path,
					search: parameterString,
					state: {
						from : path
					},
					hash: ''
				};
				this.history.push(locationObj);
			}
		}
	}

	/**
	 * Get url search params
	 * @returns {URLSearchParams} params
	 */
	private getUrlSearchParams(): URLSearchParams {
		const currentSearch = typeof window !== 'undefined' ? window.location.search : this.location.search;
		const [, search] = currentSearch.split('?');

		return new URLSearchParams(search);
	}

	/**
	 * Returns a comparison ready URL by decoding it to handle special characters
	 * @param {string} url
	 * @returns {string} url
	 */
	private getComparableUrl(url: string): string {
		return decodeUriComponentWithTryCatch(url);
	}
}

export default new PlpUrlModifier();
